import {
  Button,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from "@mui/material";
import { useState } from "react";
import {
  BranchData,
  EmployeeData,
} from "../../services/dto/response/ratings-response-dto";
import { useTranslation } from "react-i18next";
import { getBranchDisplayName } from "../../utils/branch-display-name";

interface MessageFilterProps {
  selectedBranch: number | null;
  selectedEmployee: number | null;
  setSelectedBranch: (branchId: number | null) => void;
  setSelectedEmployee: (employeeId: number | null) => void;
  branches: BranchData[];
  employees: EmployeeData[];
}

export function MessageFilter({
  selectedBranch,
  setSelectedBranch,
  selectedEmployee,
  setSelectedEmployee,
  branches,
  employees,
}: MessageFilterProps) {
  const [branchAnchorEl, setBranchAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [groupAnchorEl, setGroupAnchorEl] = useState<HTMLElement | null>(null);

  const { t } = useTranslation();
  const selectedBranchOption =
    selectedBranch !== null
      ? branches.find((branch) => branch.branchId === selectedBranch)
      : undefined;
  const selectedBranchLabel = selectedBranchOption
    ? getBranchDisplayName(selectedBranchOption) || t("messages.filter.branch")
    : t("messages.filter.branch");

  const handleBranchClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setBranchAnchorEl(e.currentTarget);
  };
  const handleGroupClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setGroupAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setBranchAnchorEl(null);
    setGroupAnchorEl(null);
  };

  const handleBranchSelect = async (branchId: number) => {
    setSelectedBranch(branchId);
    handleClose();
  };

  const handleEmployeeSelect = (employeeId: number) => {
    setSelectedEmployee(employeeId);
    handleClose();
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-[#898989] text-sm font-semibold">
        {t("messages.filter.title")}
      </span>
      <div className="flex gap-4">
        <div>
          <Button
            id="filter-branch-button"
            title={selectedBranchLabel}
            variant="contained"
            disableElevation
            onClick={handleBranchClick}
            sx={{
              background: "#2D2F32",
              margin: 0,
              padding: "0 10px",
              borderRadius: "999px",
              textTransform: "none",
              fontFamily: "Montserrat",
              fontWeight: 300,
              maxWidth: "150px",
            }}
            endIcon={
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                stroke="#EF0BB8"
                strokeWidth="0"
              >
                <path
                  d="M5.707 9.71a1 1 0 0 0 0 1.415l4.892 4.887a2 2 0 0 0 2.828 0l4.89-4.89a1 1 0 1 0-1.414-1.415l-4.185 4.186a1 1 0 0 1-1.415 0L7.121 9.71a1 1 0 0 0-1.414 0Z"
                  fill="#EF0BB8"
                  stroke="none"
                />
              </svg>
            }
          >
            <div className="truncate">{selectedBranchLabel}</div>
          </Button>
          <Popper
            open={Boolean(branchAnchorEl)}
            anchorEl={branchAnchorEl}
            role={undefined}
            transition
            disablePortal
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === "bottom" ? "center top" : "center bottom",
                }}
              >
                <Paper
                  sx={{
                    background: "#2D2F32",
                    marginTop: "2px",
                    maxHeight: "200px",
                    overflowY: "auto",
                    maxWidth: "200px",
                  }}
                  className="custom-scrollbar"
                >
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList>
                      {branches.map((branch) => (
                        <MenuItem
                          sx={{
                            background: "#2D2F32",
                            color: "white",
                            fontFamily: "Montserrat",
                            fontSize: "16px",
                          }}
                          key={branch.branchId}
                          onClick={() => handleBranchSelect(branch.branchId)}
                        >
                          <span className="truncate" title={getBranchDisplayName(branch)}>
                            {getBranchDisplayName(branch)}
                          </span>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </div>
        <Button
          id="filter-employee-button"
          variant="contained"
          title={
            selectedEmployee !== null
              ? employees.find(
                  (employee) => employee.userId === selectedEmployee
                )?.name || t("messages.filter.employee")
              : t("messages.filter.employee")
          }
          disableElevation
          onClick={handleGroupClick}
          sx={{
            background: "#2d2f32",
            margin: 0,
            padding: "0 10px",
            borderRadius: "999px",
            textTransform: "none",
            fontFamily: "Montserrat",
            fontWeight: 300,
            maxWidth: "150px",
            "&.Mui-disabled": {
              backgroundColor: "rgba(45, 47, 50, 0.5)",
              color: "rgba(255, 255, 255, 0.5)",
              "& svg": {
                opacity: 0.5,
              },
            },
          }}
          endIcon={
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              stroke="#EF0BB8"
              strokeWidth="0"
            >
              <path
                d="M5.707 9.71a1 1 0 0 0 0 1.415l4.892 4.887a2 2 0 0 0 2.828 0l4.89-4.89a1 1 0 1 0-1.414-1.415l-4.185 4.186a1 1 0 0 1-1.415 0L7.121 9.71a1 1 0 0 0-1.414 0Z"
                fill="#EF0BB8"
                stroke="none"
              />
            </svg>
          }
        >
          <div className="truncate">
            {selectedEmployee !== null
              ? employees.find(
                  (employee) => employee.userId === selectedEmployee
                )?.name || t("messages.filter.employee")
              : t("messages.filter.employee")}
          </div>
        </Button>
        <Popper
          open={Boolean(groupAnchorEl)}
          anchorEl={groupAnchorEl}
          role={undefined}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "center top" : "center bottom",
              }}
            >
              <Paper
                sx={{
                  background: "#2D2F32",
                  marginTop: "2px",
                  maxHeight: "200px",
                  overflowY: "auto",
                  maxWidth: "200px",
                }}
                className="custom-scrollbar truncate"
              >
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList>
                    {employees.map((employee) => (
                      <MenuItem
                        sx={{
                          background: "#2D2F32",
                          color: "white",
                          fontFamily: "Montserrat",
                          fontSize: "16px",
                        }}
                        key={employee.userId}
                        onClick={() => handleEmployeeSelect(employee.userId)}
                      >
                        <span title={employee.name} className="truncate">
                          {employee.name}
                        </span>
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>
    </div>
  );
}
